import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react';

import classnames from 'classnames';

import Drawer from 'components/uiLibrary/Drawer';
import Image from 'components/uiLibrary/Image';
import { TextButton } from 'components/uiLibrary/LinkButton';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

// eslint-disable-next-line import/no-cycle
import ProfileDropdown from 'components/Globals/ProfileDropdown';
import { StickyHeadersProvider, StickyHeadersContainer, StickyHeaderState } from 'components/Globals/StickyHeaders';

import { PAYWALL_STEPS, TP } from 'constants/index';
import { SUBSCRIPTION_PRODUCT_IDS, SUBSCRIPTIONS_STATUSES } from 'constants/subscriptions';

import useAppContext from 'utils/hooks/useAppContext';
import { useUserSubscriptions } from 'utils/hooks/useAuthenticatedUser';
import useDeviceTypeLayouts from 'utils/hooks/useDeviceTypeLayouts';
import { useDialogs } from 'utils/hooks/useDialogs';

import { useTranslation } from 'src/i18n';

import LogoImage from 'public/images/logo.png';

import TestimonialsAndCustomers from '../TestimonialsAndCustomers';

import classes from './PaywallDrawerWrapper.module.scss';

// eslint-disable-next-line @stylistic/curly-newline
const NOOP = () => {};

const DrawerContext = createContext({
  scrollToFooter: NOOP,
});

const StickyHeaderWrapper = ({ children, hasStickyHeaders, ifHeaderIsSticky }) => {
  if (hasStickyHeaders) {
    return (
      <div
        className={classnames(classes.container, {
          [classes.withoutBorder]: ifHeaderIsSticky('plan-group-header') && !ifHeaderIsSticky('plan-group-footer'),
        })}
      >
        <div className={classes.stickyHeader__wrapper}>{children}</div>
      </div>
    );
  }

  return children;
};

const PaywallDrawerWrapper = ({
  children,
  onClose,
  onBack,
  allowBackTransition = false,
  allowCloseButton = false,
  paywallStep,
}) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { paywall, isLoggedIn, isReturningGuestUser } = useAppContext();
  const { paymentOptionsModal, isLoginDialog } = useDialogs();
  const { data: userSubscriptions } = useUserSubscriptions();
  const { isDesktop } = useDeviceTypeLayouts();
  const contentRef = useRef(null);
  const drawerRef = useRef(null);
  const footerRef = useRef(null);

  const activeSubscriptions = useMemo(
    () => userSubscriptions?.filter(subscription => subscription?.status === SUBSCRIPTIONS_STATUSES.active) || [],
    [userSubscriptions],
  );

  const hasActiveSubscription = activeSubscriptions.length > 0;
  const hasArchiveAccess = activeSubscriptions.some(
    subscription => subscription?.subscription?.product?.id === SUBSCRIPTION_PRODUCT_IDS.ARCHIVE_ACCESS,
  );

  const scrollToFooter = useCallback(() => {
    if (footerRef.current) {
      footerRef.current.scrollIntoView({ behavior: 'auto' });
    }

    // NOTE: The function is accessing footerRef.current latest values always
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextValue = useMemo(() => ({ scrollToFooter }), [scrollToFooter]);

  const onScrollToTop = useCallback(() => {
    contentRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const renderHeaderWrapper = useCallback(props => <StickyHeaderWrapper {...props} />, []);

  return (
    <StickyHeadersProvider name="paywall-drawer" renderHeaderWrapper={renderHeaderWrapper}>
      <Drawer
        drawerContentRef={node => {
          drawerRef.current = node;
        }}
        onClose={allowCloseButton && paywall?.collapsible ? onClose : () => NOOP}
        styles={{ drawerPaper: classes.drawerPaper, drawerRoot: classes.drawerRoot }}
        ModalProps={{ disableEnforceFocus: paymentOptionsModal.isOpen || isLoginDialog.isOpen }}
        isOpen
      >
        <div className={classes.root} ref={contentRef}>
          <div className={classes.header}>
            <div className={classes.container}>
              <div className={classes.header__wrapper}>
                <div className={classes.header__wrapper_left}>
                  {allowBackTransition && <SpriteIcon icon="chevron_left" size={18} onClick={onBack} />}
                  <Image src={LogoImage} alt="Operabase Home" height={14} width={140} />
                  <Typography size={12}>{t(`${TP}.LP_SINCE`)}</Typography>
                  {isDesktop && paywall?.collapsible && (
                    <Typography size={14} weight="medium" variant="p" className={classes.header__subtext}>
                      {t(`${TP}.FN_GLOBAL_REFERENCE_PERFORMING_ARTS`)}
                    </Typography>
                  )}
                </div>
                <StickyHeaderState>
                  {({ hasStickyHeaders }) => {
                    if (isLoggedIn && paywallStep === PAYWALL_STEPS.LOGIN_DRAWER) {
                      return (
                        <div className={classes.header__wrapper_right}>
                          <ProfileDropdown />
                          <SpriteIcon icon="close" size={18} onClick={onClose} />
                        </div>
                      );
                    }

                    if (hasStickyHeaders) {
                      return (
                        <div className={classes.header__wrapper_right}>
                          <TextButton onClick={onScrollToTop} rightIcon={<SpriteIcon icon="chevron_up" size={18} />}>
                            <Typography
                              size={isDesktop ? 14 : 10}
                              color="inherit"
                              weight={isDesktop ? 'medium' : 'bold'}
                            >
                              {isLoggedIn ? t(`${TP}.FN_CHOOSE_PLAN`) : t(`${TP}.m_LOGIN`)}
                            </Typography>
                          </TextButton>
                        </div>
                      );
                    }

                    if (isReturningGuestUser) {
                      return <div className={classes.header__wrapper_right} />;
                    }

                    return (
                      <div className={classes.header__wrapper_right}>
                        {allowCloseButton ? (
                          <SpriteIcon icon="close" size={18} onClick={onClose} />
                        ) : (
                          <>
                            {isLoggedIn && !hasArchiveAccess && isDesktop && (
                              <Typography size="14" color="orange">
                                ({t(`${TP}.FREE_PLAN_OPTIONS`)})
                              </Typography>
                            )}
                            {paywall?.collapsible && (
                              <TextButton onClick={onClose} rightIcon={<SpriteIcon icon="chevron_right" size={18} />}>
                                <Typography
                                  size={isDesktop ? 14 : 10}
                                  color="inherit"
                                  weight={isDesktop ? 'medium' : 'bold'}
                                >
                                  {hasActiveSubscription ? t(`${TP}.m_CONTINUE`) : t(`${TP}.CONTINUE_FREE_PAYWALL_CTA`)}
                                </Typography>
                              </TextButton>
                            )}
                            {isLoggedIn && !paywall?.collapsible && <ProfileDropdown />}
                          </>
                        )}
                      </div>
                    );
                  }}
                </StickyHeaderState>
              </div>
            </div>
            <StickyHeadersContainer />
          </div>
          <div className={classes.content}>
            <div className={classes.container}>
              <DrawerContext.Provider value={contextValue}>{children}</DrawerContext.Provider>
            </div>
          </div>
          <TestimonialsAndCustomers />
        </div>
      </Drawer>
    </StickyHeadersProvider>
  );
};

export const useDrawerContext = () => useContext(DrawerContext);
export default PaywallDrawerWrapper;
